import stampit from 'stampit';
import Utils from './utils-mixin';


const PrivilegedMethods = stampit().init(
  function() {
    let itemObj,
      infoWindowObj;

    /**
     *
     * Get item or return null if no item was created
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @function getItem
     * @instance
     *
     * @return {object} google maps item instance: marker, polygon etc.
     */
    this.getItem = function() {
      return itemObj || null;
    };

    /**
     *
     * Set main item object
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @function setItem
     * @instance
     *
     * @param {object} item google maps item instance: marker, polygon etc.
     * @return {object} The instance on which this method was called
     */
    this.setItem = function(item) {
      itemObj = item;

      return this;
    };

    /**
     *
     * Get infoWindow of item
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @function getInfoWindow
     * @instance
     *
     * @return {object|null} google.maps.InfoWindow object instance
     */
    this.getInfoWindow = function() {
      return infoWindowObj || null;
    };

    /**
     *
     * Set infoWindow of item
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @function setInfoWindow
     * @instance
     *
     * @param {object} google.maps.InfoWindow object instance
     *
     * @return {object} The instance on which this method was called
     */
    this.setInfoWindow = function(infoWindow) {
      infoWindowObj = infoWindow;

      return this;
    };
  }
);





const PublicMethods = stampit({
  methods: {

    /**
     *
     * Add marker info window and click event listener
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @param {string} content html content
     * @param {object} [options={}] additional {@link https://developers.google.com/maps/documentation/javascript/3.exp/reference#InfoWindowOptions|infoWindow options}
     *
     * @return {object} The instance on which this method was called
     */
    addInfoWindow: function addInfoWindow(content, options = {}) {
      let infoWindow,
        item = this.getItem(),
        map = this.getMap(),
        mergedOptions,
        instance = this;

      mergedOptions = Object.assign({
        content: content
      }, options);

      infoWindow = new google.maps.InfoWindow(mergedOptions);

      this.setInfoWindow(infoWindow);

      item.addListener('click', function() {
        instance.getInfoWindow().open(map, item);
      });

      return this;
    },

    /**
     *
     * Opens item window if one exists
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {object} The instance on which this method was called
     */
    openWindow: function openWindow() {
      let w = this.getInfoWindow();

      if (w) {
        w.open(this.getMap(), this.getItem());
      }

      return this;
    },

    /**
     *
     * Closes item window if one exists
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {object} The instance on which this method was called
     */
    closeWindow: function openWindow() {
      let w = this.getInfoWindow();

      if (w) {
        w.close();
      }

      return this;
    },

    /**
     *
     * Get map to which item is currently attached
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {object|null} google.maps.Map object instance
     */
    getMap: function getMap() {
      let item = this.getItem(),
        map;

      if (item) {
        map = item.getMap();
      }

      return map || null;
    },

    /**
     *
     * Set map of item
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @param {object} google.maps.Map object instance
     *
     * @return {object} The instance on which this method was called
     */
    setMap: function setMap(map) {
      let item = this.getItem();

      if (item) {
        item.setMap(map);
      }

      return this;
    },

    /**
     *
     * Show item
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {object} The instance on which this method was called
     */
    show: function show() {
      this.getItem().setVisible(true);

      return this;
    },

    /**
     *
     * Hide item
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {object} The instance on which this method was called
     */
    hide: function hide() {
      this.getItem().setVisible(false);

      return this;
    },

    /**
     *
     * Checks if item is hidden
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {bool} if item is hidden or not
     */
    isHidden: function isHidden() {
      let hidden = !this.isVisible();

      return hidden;
    },

    /**
     *
     * Checks if item is visible
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @return {bool} if item is visible or not
     */
    isVisible: function isVisible() {
      let visible = this.getItem().getVisible();

      return visible;
    },

    /**
     *
     * Set options of item
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @param {object} options all available google maps item options
     * @see fx https://developers.google.com/maps/documentation/javascript/3.exp/reference#MarkerOptions
     *
     * @return {object} The instance on which this method was called
     */
    setOptions: function setOptions(options) {
      if (options) {
        this.getItem().setOptions(options);
      }

      return this;
    },

    /**
     *
     * Parses options object and converts values to instances
     * of google.maps.Point and google.maps.Size
     *
     * @memberOf MapApi/Google/ItemApiMixin
     * @instance
     *
     * @param {object}   options                options object
     * @param {string}   options.url            url of icon
     * @param {number[]} [options.size]         natural width/height of icon, fx [40,80]
     * @param {number[]} [options.scaledSize]   scaled width/height of icon, fx [20,40] for 50% scaling
     * @param {number[]} [options.anchor]       xy coordinates of marker anchor relative to icon & scale, fx [20,40]
     * @param {number[]} [options.origin]       xy coordinates of icon on image, fx [0,100]. Mainly used when icon image is a sprite
     * @param {number[]} [options.labelOrigin]  xy coordinates of label relative to icon
     *
     * @see https://developers.google.com/maps/documentation/javascript/3.exp/reference#Icon
     *
     * @return {object}  parsed object which can be used as icon
     */
    createIcon: function createIcon(options){

      let parsedOptions;

      if (options && options.url) {

        let size = options.size,
          scaledSize = options.scaledSize,
          anchor = options.anchor,
          origin = options.origin,
          labelOrigin = options.labelOrigin;

        parsedOptions = {
          url: options.url
        };

        if (size) {
          parsedOptions.size = new google.maps.Size(size[0], size[1]);
        }

        if (scaledSize) {
          parsedOptions.scaledSize = new google.maps.Size(scaledSize[0], scaledSize[1]);
        }

        if (anchor) {
          parsedOptions.anchor = new google.maps.Point(anchor[0], anchor[1]);
        }

        if (origin) {
          parsedOptions.origin = new google.maps.Point(origin[0], origin[1]);
        }

        if (labelOrigin) {
          parsedOptions.labelOrigin = new google.maps.Point(labelOrigin[0], labelOrigin[1]);
        }
      }

      return parsedOptions;
    },
  }
});

const ItemApiMixin = stampit(PrivilegedMethods, PublicMethods, Utils);

/**
 *
 * Google item api mixin created using stampit.
 *
 * Serves as base mixin for all google map item type
 * factories like markers, polygons etc.
 *
 * @mixin MapApi/Google/ItemApiMixin
 *
 * @example
 * import ItemApiMixin from './map-api/google/item-api-mixin';
 *
 * // create new item type factory
 * let NewItemStamp = stampit(ItemApiMixin, SomeNewProps, SomeNewMethods)
 *
 * let newItemHandler = NewItemStamp.create()
 */
export default ItemApiMixin;



// WEBPACK FOOTER //
// ./src/frontend/js/map-api/google/item-api-mixin.js